import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/styles";
import { FormControlLabel, Checkbox, Snackbar, Divider } from "@material-ui/core";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import { Alert } from "@material-ui/lab";
import { debounce } from "lodash";

import uiConf from "../../config/uiConf";
import defineConf from "../../config/defineConf";

import apiClient from "../../lib/common/apiClient";
import checkValidation from "../../lib/utils/checkValidation";
import checkConditionWithMsg from "../../lib/utils/checkConditionWithMsg";

import Dialog from "../dialog/functional/Dialog";
import DialogHeader from "../dialog/functional/DialogHeader";
import DialogContent from "../dialog/functional/DialogContent";
import DialogTitle from "../dialog/functional/DialogTitle";
import DialogActionsOkCancel from "../dialog/functional/DialogActionsOkCancel";
import AutoForm from "../form/functional/AutoForm";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import getExpirationDate from "../../lib/utils/getExpirationDate";
import failureKey from "../../lib/common/failureKey";

const useStyles = makeStyles((theme) => ({
    keepOpenSwitch: {
        marginLeft: "8px",
    },

    label: {
        fontSize: "smaller",
        color: theme.palette.primary.text,
    },
}));

function MomSysCustomerSupplyReturnDialog(props) {
    const {
        id,
        schema,

        moveable,
        resizable,
        fullWidth,
        maxWidth,

        onClose,
        onInitialized,
        onSuccess,

        title,
        formId,

        onDialogUpdateDetail,
        onSelectMst,

        customizeErrorMsgCallback,

        ...others
    } = props;

    const classes = useStyles();
    const { t } = useTranslation();

    const [dialogInstance, setDialogInstance] = useState();
    const [okButtonDisabled, setOkButtonDisabled] = useState(true);
    const [formData, setFormData] = useState();
    const [keepOpen, setKeepOpen] = useState(false);

    const [momSysZvwMaterialGrid, setMomSysZvwMaterialGrid] = useState();
    const [, setMomSysZvwMaterialApi] = useState();
    const [momSysZvwMaterialSelect, setMomSysZvwMaterialSelect] = useState();

    const [notification, setNotification] = useState({
        open: false,
        severity: "success",
        msg: "",
    });

    const checkItemValidataion = (schema, formData) => {
        for (const column of schema.columns) {
            let [valid, msg] = checkValidation("edit", column, column.validation, formData[column.name] ?? "");

            if (!valid) {
                nofifyWarning(msg);
                return false;
            }
        }

        return true;
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const nofifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onNotifySuccess = (msg) => {
        notifySuccess(msg);

        if (onDialogUpdateDetail) {
            onDialogUpdateDetail();
        }
    };

    useEffect(() => {
        (async () => {
            if (momSysZvwMaterialGrid) {
                momSysZvwMaterialGrid.selectionChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setMomSysZvwMaterialSelect(grid.selectedItems[0]);
                        } else {
                            setMomSysZvwMaterialSelect();
                        }
                    }, 10)
                );

                momSysZvwMaterialGrid.itemsSourceChanged.addHandler(
                    debounce((grid, event) => {
                        if (grid.selectedItems && grid.selectedItems[0]) {
                            setMomSysZvwMaterialSelect(grid.selectedItems[0]);
                        } else {
                            setMomSysZvwMaterialSelect();
                        }
                    }, 10)
                );
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [momSysZvwMaterialGrid]);

    useEffect(() => {
        if (momSysZvwMaterialSelect) {
            setFormData((formData) => {
                const newFormData = {
                    ...formData,

                    orderBuyerId: momSysZvwMaterialSelect.orderBuyerId,
                    companyCode: momSysZvwMaterialSelect.companyCode,
                    materialCode: momSysZvwMaterialSelect.materialCode,
                    materialCodeVer: momSysZvwMaterialSelect.materialCodeVer,
                    materialName: momSysZvwMaterialSelect.materialName,
                    itemSpecName: momSysZvwMaterialSelect.itemSpecName,
                    itemUnitName: momSysZvwMaterialSelect.itemUnitName,
                    qtyReturn: momSysZvwMaterialSelect.qtyCurrent,

                    returnDate: new Date(),
                    manufacturedDate: new Date(),
                    expirationDate: getExpirationDate(momSysZvwMaterialSelect.standardExpirationDate, new Date()),
                };

                for (const column of schema.columns) {
                    if (column?.defaultValue !== undefined) {
                        newFormData[column.name] = column.defaultValue;
                    }
                }

                return newFormData;
            });
        }
    }, [schema, momSysZvwMaterialSelect]);

    const onDialogInitialized = (instance) => {
        setDialogInstance(instance);

        if (onInitialized) {
            onInitialized(instance);
        }
    };

    const onApiSuccess = (result) => {
        setKeepOpen((keepOpen) => {
            if (!keepOpen) {
                dialogInstance.hide();
            }

            if (onSuccess) {
                setTimeout(() => onSuccess(result.data), 0);
            } else {
                onNotifySuccess(result.data);
            }

            return keepOpen;
        });
    };

    const onApiFailure = (reason) => {
        if (customizeErrorMsgCallback) {
            customizeErrorMsgCallback(reason);
        } else {
            notifyFailure(
                t(failureKey("insert", reason.response?.status), { reason: reason.response ? reason.response.data : JSON.stringify(reason) })
            );
        }
    };

    const onOk = (schema, formData) => {
        if (!checkItemValidataion(schema, formData)) {
            console.error("Form Validation Failed");

            return;
        }

        if (schema.validation) {
            let [valid, msg] = checkConditionWithMsg(formData, schema.validation);

            if (!valid) {
                nofifyWarning(t("warning.formDataValidationFail", { msg: msg }));
                return;
            }
        }

        
        // build base api url

        const url = `/api/${schema.name}`;

        const apiData = { ...formData, customerSupplyInputId: momSysZvwMaterialGrid.selectedItems[0]?.customerSupplyInputId };
        
        const form = new FormData();

        const json = {};
        const deleted = {};

        if (schema && !schema.columnMap) {
            let columnMap = {};
            schema.columns.map((column) => (columnMap[column.name] = column));
            schema.columnMap = columnMap;
        }

        for (const formItem in apiData) {
            if (schema.columnMap && schema.columnMap[formItem] && schema.columnMap[formItem].formItemType === "file") {
                deleted[formItem] = apiData[formItem].deletedFiles;

                for (const file of apiData[formItem].activeFiles) {
                    if (file.type === "url") continue;

                    form.append(formItem, file.file);
                }
            } else {
                if (apiData[formItem] !== undefined) {
                    json[formItem] = apiData[formItem];
                }
            }
        }

        // for (const formItem in apiData) {
        //     if (apiData[formItem] !== undefined) {
        //         json[formItem] = apiData[formItem];
        //     }
        // }

        form.append("deleted", JSON.stringify(deleted));
        form.append("json", JSON.stringify(json));

        apiClient
            .post(url, form, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((result) => onApiSuccess(result))
            .catch((reason) => onApiFailure(reason));
    };

    const onCancel = () => {
        setMomSysZvwMaterialSelect();

        setOkButtonDisabled(true);

        setFormData();

        dialogInstance.hide();
    };

    const checkKeepOpen = () => {
        return (
            <>
                <FormControlLabel
                    control={
                        <Checkbox
                            className={classes.keepOpenSwitch}
                            checked={keepOpen}
                            color="primary"
                            onChange={() => setKeepOpen((keepOpen) => !keepOpen)}
                            name="checkKeepOpen"
                            size="small"
                        />
                    }
                    label={<span className={classes.label}>{t("term.keepDialogOpen")}</span>}
                />
                <div style={{ flexGrow: 1 }}></div>
            </>
        );
    };

    const onChangeFormData = (formData) => {

        setOkButtonDisabled(false);

        setFormData(formData);

    };

    const onCloseNotification = () => {
        setNotification((notification) => {
            return { ...notification, open: false };
        });
    };

    const onCloseDialog = () => {
        setMomSysZvwMaterialSelect();

        setOkButtonDisabled(true);

        setFormData();

        if (onClose) {
            onClose();
        }
    };

    return (
        <Dialog
            id={id}
            moveable={moveable ? moveable : uiConf.dialogMoveable}
            resizable={resizable ? resizable : uiConf.dialogResizable}
            fullWidth={fullWidth ? fullWidth : uiConf.dialogFullWidth}
            maxWidth="lg"
            onClose={onCloseDialog}
            onInitialized={onDialogInitialized}
            {...others}
        >
            <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
            </DialogHeader>

            <Divider />

            <DialogContent style={{ padding: "0px", height: "600px" }}>
                <ReflexContainer orientation="vertical">
                    <ReflexElement flex={0.6}>
                        <BasicCrudScreenType
                            id="MomSysVwCustomerSupplyMaterialReturnable"
                            headerTitle={t("tableName.MomSysVwCustomerSupplyMaterialReturnable")}
                            view="MomSysVwCustomerSupplyMaterialReturnable"
                            noCreate
                            noEdit
                            noDelete
                            noExcel
                            onInitialized={(grid) => {
                                grid.selectionMode = "Row";
                                setMomSysZvwMaterialGrid(grid);
                            }}
                            onApiInitialized={(api) => {
                                setMomSysZvwMaterialApi(api);
                            }}
                            embededInOtherScreenType
                        />
                    </ReflexElement>

                    <ReflexSplitter style={{ width: "1px", color: "lightgray" }} />

                    <ReflexElement flex={0.4}>
                        <div
                            style={{
                                padding: "16px",
                            }}
                        >
                            <AutoForm
                                id={formId}
                                schema={schema}
                                mode="edit"
                                initialData={formData}
                                onChange={onChangeFormData}
                                modifyFormDataCallback={(formData, name) => {
                                    if (formData.qcResult === defineConf.qcResult.QC_OK) {
                                        formData.defectiveCode = "INS000";
                                    }

                                    if (name === "manufacturedDate") {
                                        formData.expirationDate = getExpirationDate(
                                            momSysZvwMaterialSelect.standardExpirationDate,
                                            new Date(formData.manufacturedDate)
                                        );
                                    }
                                }}
                            />
                        </div>
                    </ReflexElement>
                </ReflexContainer>

                <Snackbar
                    open={notification.open && notification.severity !== "success"}
                    autoHideDuration={uiConf.dialogNotifyAutoHideDuration}
                    onClose={onCloseNotification}
                >
                    <Alert onClose={onCloseNotification} variant="filled" severity={notification.severity}>
                        {notification.msg}
                    </Alert>
                </Snackbar>
            </DialogContent>

            <Divider />

            <DialogActionsOkCancel
                firstActionComponent={checkKeepOpen}
                labels={[t("common.add")]}
                buttonDisabled={[okButtonDisabled, false]} /* [okButtonDisabled, cancelButtonDisabled] */
                onOk={() => onOk(schema, formData)}
                onCancel={onCancel}
            />
        </Dialog>
    );
}

export default MomSysCustomerSupplyReturnDialog;
